import React from 'react';
import { Copy, Server, X } from 'lucide-react';

interface McpSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode?: boolean;
  title?: string;
}

const MCP_JSON = `{
  "mcpServers": {
    "MLST ReScript": {
      "url": "https://app.rescript.info/api/v1/mcp"
    }
  }
}`;

const McpSetupModal: React.FC<McpSetupModalProps> = ({
  isOpen,
  onClose,
  isDarkMode = false,
  title = 'MLST ReScript MCP',
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={`w-full max-w-2xl rounded-2xl border p-6 shadow-2xl ${
          isDarkMode
            ? 'border-gray-700 bg-gray-800 text-gray-100'
            : 'border-gray-200 bg-white text-gray-900'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`rounded-2xl p-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <Server className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{title}</h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Connect your client to the MLST ReScript MCP endpoint.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-lg p-2 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 text-sm">
          <div className={`rounded-xl border p-4 ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="mb-2 font-medium">Basic setup</div>
            <ol className="list-decimal space-y-1 pl-5">
              <li>Open your MCP-capable client settings.</li>
              <li>Add a new server named <strong>MLST ReScript</strong>.</li>
              <li>Use <code>https://app.rescript.info/api/v1/mcp</code> as the server URL.</li>
              <li>Authenticate in the app if your client prompts for normal browser login.</li>
            </ol>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="font-medium">JSON snippet</div>
              <button
                type="button"
                onClick={() => { void navigator.clipboard.writeText(MCP_JSON); }}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <Copy className="h-4 w-4" />
                <span>Copy JSON</span>
              </button>
            </div>
            <pre
              className={`overflow-x-auto rounded-xl border p-4 text-xs ${
                isDarkMode
                  ? 'border-gray-700 bg-gray-900/70 text-gray-200'
                  : 'border-gray-200 bg-gray-50 text-gray-800'
              }`}
            >
              {MCP_JSON}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default McpSetupModal;
